import { Component, OnInit, OnDestroy, Renderer2, AfterViewInit, NgZone, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Logger, untilDestroyed } from '@core';
import { I18nService } from '@app/i18n';
import { LoaderService } from '../services/loader.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Store } from '@ngrx/store';
import { setTheme } from '../state/theme.actions';

const log = new Logger('App');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  public showLoader: boolean;
  deviceInfo: any;
  private imgObserver?: MutationObserver;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private loaderService: LoaderService,
    private deviceService: DeviceDetectorService,
    private renderer: Renderer2,
    private store: Store,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.epicFunction();
    // Initialize theme from localStorage on app start
    this.initializeTheme();
  }

  private initializeTheme(): void {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const theme = savedTheme || 'light';
    this.store.dispatch(setTheme({ theme }));
  }

  epicFunction() {
    this.deviceInfo = this.deviceService.getDeviceInfo();
    const isMobile = this.deviceService.isMobile();
    const isTablet = this.deviceService.isTablet();
    const isDesktopDevice = this.deviceService.isDesktop();

    var device = { isMobile, isTablet, isDesktopDevice };
  }

  ngOnInit() {
    const userAgent = window.navigator.userAgent;
    var screenWidth = window.innerWidth;
    var agentType = 'Desktop';

    if (screenWidth < 768) {
      agentType = 'Mobile';
    } else if (screenWidth >= 768 && screenWidth <= 1024) {
      agentType = 'Tablet';
    } else {
      agentType = 'Desktop';
    }

    this.loaderService.status.subscribe((val: boolean) => {
      Promise.resolve().then(() => (this.showLoader = val));
    });

    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');

    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    const onNavigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this)
      )
      .subscribe((event) => {
        const title = event.title;
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });

    // Scroll reset logic
    this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        window.scrollTo(0, 0);
      }
    });

    // Add classes to <body> based on the route and language
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((evt: NavigationEnd) => {
      // Remove previous page and language classes
      this.renderer.removeClass(document.body, 'home-page');
      this.renderer.removeClass(document.body, 'other-page');
      this.renderer.removeClass(document.body, 'DE');
      this.renderer.removeClass(document.body, 'IT');

      // Add page-specific class
      if (evt.urlAfterRedirects === '/home') {
        this.renderer.addClass(document.body, 'home-page');
      } else {
        this.renderer.addClass(document.body, 'other-page');
      }

      // Add language-specific class
      const currentLang = this.translateService.currentLang; // Get the current language
      if (currentLang) {
        this.renderer.addClass(document.body, currentLang.toUpperCase()); // Adds `DE` or `IT` (e.g., `DE` or `IT` as a class)
      }
    });
  }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      const fixAlts = () => {
        document
          .querySelectorAll<HTMLImageElement>('img.asset__img:not([alt]), img.creole-text:not([alt])')
          .forEach((img) => img.setAttribute('alt', img.getAttribute('aria-label') || ''));
      };

      fixAlts();
      this.imgObserver = new MutationObserver(fixAlts);
      this.imgObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'src'],
      });
    });
  }

  ngOnDestroy() {
    this.i18nService.destroy();
    this.imgObserver?.disconnect();
    this.loaderService.status.observers.forEach(function (element) {
      element.complete();
    });
  }
}
